import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthShell } from '../components/AuthShell';
import { Field, ErrorBanner } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatPhone } from '../lib/format';

export function CompleteProfilePage() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next');
  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [lastName, setLastName] = useState(user?.lastName ?? '');
  const [phone, setPhone] = useState(user?.phone ? formatPhone(user.phone) : '');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [confirmedAge, setConfirmedAge] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const digits = phone.replace(/\D/g, '');
      await api.patch('/auth/me', {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: digits.length ? digits : null,
        acceptedTerms,
        confirmedAge,
      });
      await refresh();
      navigate(next ? decodeURIComponent(next) : '/', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Unable to save your profile. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell title="Almost there" subtitle="Tell us who you are so we can set up your picks.">
      <form onSubmit={onSubmit} noValidate>
        {error && (
          <div className="mb-4">
            <ErrorBanner message={error} />
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name" htmlFor="firstName">
            <input id="firstName" required className="input" autoComplete="given-name"
              value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          </Field>
          <Field label="Last name" htmlFor="lastName">
            <input id="lastName" required className="input" autoComplete="family-name"
              value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </Field>
        </div>
        <Field label="Phone" htmlFor="phone" hint="Optional. US 10-digit number.">
          <input id="phone" type="tel" className="input" autoComplete="tel"
            placeholder="(208) 555-1234" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </Field>

        <div className="mb-4 mt-2 space-y-3">
          <label htmlFor="acceptedTerms" className="flex items-start gap-2 text-sm text-ink">
            <input
              id="acceptedTerms"
              type="checkbox"
              className="mt-0.5 h-4 w-4 shrink-0"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
            />
            <span>
              I have read and agree to the{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="font-medium text-grove underline">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="font-medium text-grove underline">
                Privacy Policy
              </a>
              .
            </span>
          </label>
          <label htmlFor="confirmedAge" className="flex items-start gap-2 text-sm text-ink">
            <input
              id="confirmedAge"
              type="checkbox"
              className="mt-0.5 h-4 w-4 shrink-0"
              checked={confirmedAge}
              onChange={(e) => setConfirmedAge(e.target.checked)}
            />
            <span>I confirm that I am 21 years of age or older.</span>
          </label>
        </div>

        <button
          type="submit"
          className="btn-primary btn-lg w-full"
          disabled={busy || !acceptedTerms || !confirmedAge}
        >
          {busy ? 'Saving…' : 'Save and continue'}
        </button>
      </form>
    </AuthShell>
  );
}
